import { TimeAxisRangeInput, TimeAxisType } from "@/gql/types";
import { computed } from "@vue/composition-api";
import moment from "moment";
import { TimeFrameHelper, TIME_FRAME } from "../models";
import { TimeFrameOption } from "./models";
import { state } from "./state";

const { action, getter, m, mutation } = state;

//#region  timeframe
export const isWeekTimeFrame = getter(
  (s) => s.selectedTimeFrame.type === TimeAxisType.Week,
);
const IsoToYMD = (iso: string): string => iso.split("T")[0];
export const timeFrameOptions = getter((s) => {
  if (s.user.paymentEdition.timeFrameFull) {
    return s.timeFrameOptions;
  } else {
    // disabling time_frames above current month
    return s.timeFrameOptions.map((frame, index) => {
      if (index > 3) {
        return {
          ...frame,
          disable: true,
        };
      }
      return frame;
    });
  }
});

export const selectedTimeFrameOption = getter((s) => s.selectedTimeFrame);
// @ts-ignore
window.timeframes = () => timeFrameOptions.value.map((d) => d.from);
export const get_last_week_time_frame = () => timeFrameOptions.value[1];
export const get_this_week_time_frame = () => timeFrameOptions.value[0];
export const selectedTimeFrameDisplayName = getter(
  (s) => s.selectedTimeFrame.displayName,
);
export const setSelectedTimeFrameOption = m.set("selectedTimeFrame");

/**
 * 
if the user is on monday 10/6 then:
  - current week (10/6 - 10/6)
  - last week (3/6 - 9/6 ) from passed week monday to current week sunday

if the user is on sunday 9/6 then:
  - current week (3/6 - 9/6) from passed week monday to current week sunday
  - last week (27/5 - 2/6 ) monday 2 week ago sunday last week a go. 
 * 
 * */
function getWeekText(currentDate: moment.Moment) {
  const isSunday = currentDate.day() === 0;

  const currentWeekStart = isSunday
    ? moment(currentDate).subtract(1, "week").startOf("week").add(1, "day")
    : moment(currentDate).startOf("week").add(1, "day");

  const lastWeekStart = moment(currentWeekStart)
    .subtract(1, "week")
    .startOf("week")
    .add(1, "day");
  const lastWeekEnd = moment(currentWeekStart)
    .subtract(1, "week")
    .endOf("week");

  if (lastWeekEnd.day() === 6) {
    lastWeekEnd.add(1, "day");
  }

  const currentWeekText = `Current week (${currentWeekStart.format(
    "DD/MM",
  )} - ${currentDate.format("DD/MM")})`;
  const lastWeekText = `Last week (${lastWeekStart.format(
    "DD/MM",
  )} - ${lastWeekEnd.format("DD/MM")})`;

  return {
    currentWeekText,
    lastWeekText,
  };
}
// @ts-ignore
window.test_week_text = (date: string) => {
  return getWeekText(moment(date, "DD/MM/YYYY"));
};

export const setTimeFrameOptions = mutation<{ from: string; to: string }>(
  "set time frame options",
  (s, { from, to }) => {
    // from
    const fromTimeInput = IsoToYMD(from);
    const fromMonth = moment(fromTimeInput).month();
    const fromYear = moment(fromTimeInput).year();

    const current = moment();
    const additionalMonths = moment([current.year(), current.month(), 1]).diff(
      moment([fromYear, fromMonth, 1]),
      "months",
    );
    const jan1OfCurrentMonth = moment([
      current.year(),
      current.month(),
      1,
    ]).format("YYYY-MM-DD");

    const { currentWeekText, lastWeekText } = getWeekText(moment());

    const thisWeek = {
      type: TimeAxisType.Week,
      from: moment().format("YYYY-MM-DD"),
      displayName: currentWeekText,
      disable: moment(to).isBefore(moment().subtract(1, "week")),
    };

    const lastWeek = {
      type: TimeAxisType.Week,
      from: moment().subtract(1, "week").format("YYYY-MM-DD"),
      displayName: lastWeekText,
      disable: moment(to).isBefore(moment().subtract(2, "week")),
    };

    const thisMonth = {
      type: TimeAxisType.Month,
      from: moment(jan1OfCurrentMonth).format("YYYY-MM-DD"),
      displayName: moment(jan1OfCurrentMonth).format("MMMM YYYY"),
      disable: moment(to).isBefore(jan1OfCurrentMonth),
    };
    return {
      ...s,
      timeFrameOptions: [
        thisWeek,
        lastWeek,
        thisMonth,
        // other months
        ...Array(additionalMonths)
          .fill({})
          .map((_, index) => {
            const d = moment(jan1OfCurrentMonth).subtract(index + 1, "month");
            return {
              type: TimeAxisType.Month,
              from: d.format("YYYY-MM-DD"),
              displayName: d.format("MMMM YYYY"),
              disable: moment(to).isBefore(d),
            };
          }),
      ],
    };
  },
);

export const timeFrame = getter((s) => s.timeFrame);
export const calculatedTimeFrame = getter((s) => {
  const { type, from } = s.selectedTimeFrame;
  return {
    total: TimeFrameHelper.asTotalAxisRangeInput(),
    frame: {
      type,
      values: [from],
    }, //TimeFrameHelper.asTimeAxisRangeInput(s.timeFrame),
  };
});

export const displayedTimeFrame = getter((s) => {
  const today = moment();
  switch (s.timeFrame) {
    case TIME_FRAME.THIS_WEEK:
      return (
        today.subtract(1, "week").format("ll") + " - " + today.format("ll")
      );
    case TIME_FRAME.THIS_MONTH:
      return (
        today.subtract(1, "month").format("ll") + " - " + today.format("ll")
      );
    case TIME_FRAME.LAST_WEEK:
      return (
        today.subtract(2, "week").format("ll") +
        " - " +
        today.subtract(1, "week").format("ll")
      );
    case TIME_FRAME.LAST_MONTH:
    default:
      return (
        today.subtract(2, "month").format("ll") +
        " - " +
        today.subtract(1, "month").format("ll")
      );
  }
});
//const setTimeFrame = m.set("timeFrame");
export const changeTimeFrame = action<TimeFrameOption>(
  "change time frame",
  async ({ mutate }, t) => {
    console.log("changeTimeFrame : ", t);
    mutate(setSelectedTimeFrameOption, t);
  },
);

export const time_frame = {
  to_last_week: async (): Promise<TimeAxisRangeInput> => {
    const last_week = moment().subtract(1, "week").format("YYYY-MM-DD");
    const option = timeFrameOptions.value.find((l) => l.from === last_week);
    console.log("timeFrameOptions : ", timeFrameOptions.value);
    console.log("to_last_week : ", option);
    if (option) {
      await changeTimeFrame(option);
    }
    return {
      type: TimeAxisType.Week,
      values: [last_week],
    };
  },
};

const is_before_date_of_new_piis = (date: string) =>
  moment(date, "YYYY-MM-DD").isBefore("2025-05-01");

export const is_current_timeframe_before_date_of_new_piis = computed(() => {
  return is_before_date_of_new_piis(selectedTimeFrameOption.value.from);
});
